import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Search } from 'lucide-react';
import { fetchPropertySaves } from '../api/analyticsApi';
import { formatMelbourneShortDate, formatMelbourneClock } from '../../../utils/melbourneTime';

/**
 * What the Save Rate card opens: every save in the range, newest first - who
 * saved which property, and when (Christian Gravias, Monday 13391600148: "We
 * need to see which buyers are saving which properties & when. Thats the only
 * info on the save rate that matters"). The header repeats the card's own
 * savers / viewers so the two always agree.
 */
const FILTERS = [
    { id: 'all', label: 'All', test: () => true },
    { id: 'saved', label: 'Still saved', test: (s) => s.still_saved },
    { id: 'removed', label: 'Removed', test: (s) => !s.still_saved },
];

const PropertySavesModal = ({ isOpen, onClose, startDate, endDate, onBuyerClick }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setSearchTerm('');
        setFilter('all');
        setLoading(true);
        setError(null);
        fetchPropertySaves(startDate, endDate)
            .then((res) => { if (!cancelled) setData(res); })
            .catch(() => { if (!cancelled) setError('Failed to load saves'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, startDate, endDate]);

    const saves = useMemo(() => data?.saves || [], [data]);

    const visibleSaves = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const { test } = FILTERS.find((f) => f.id === filter);
        return saves.filter((s) => test(s) && (!term || [
            s.buyer_name, s.buyer_email, s.buyer_phone, s.address, s.agent_name,
        ].some((v) => v?.toLowerCase().includes(term))));
    }, [saves, searchTerm, filter]);

    if (!isOpen) return null;

    const viewers = data?.viewers || 0;
    const savers = data?.savers || 0;
    const pct = viewers ? Math.round((savers / viewers) * 1000) / 10 : 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Save Rate</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Every property saved in this range: which buyer saved it, and when.
                        </p>
                        {!loading && !error && data && (
                            <p className="text-sm text-gray-900 font-medium mt-2">
                                {savers.toLocaleString()} of {viewers.toLocaleString()} buyers who viewed a property
                                saved one ({pct.toFixed(1)}%) · {(data.count || 0).toLocaleString()}{' '}
                                save{data.count === 1 ? '' : 's'}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Filters */}
                <div className="px-6 pt-4 flex flex-col md:flex-row gap-3 md:items-center">
                    <div className="flex gap-2 flex-wrap">
                        {FILTERS.map((f) => (
                            <button
                                key={f.id}
                                onClick={() => setFilter(f.id)}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === f.id
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {f.label} ({saves.filter(f.test).length.toLocaleString()})
                            </button>
                        ))}
                    </div>
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by buyer, address or agent..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-600 py-8">{error}</div>
                    ) : visibleSaves.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            {searchTerm ? 'No saves match your search' : 'No properties saved in this range'}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {['Saved', 'Buyer', 'Property', 'Listing Agent', 'Status'].map((h) => (
                                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {visibleSaves.map((s) => (
                                        <tr
                                            key={s.id}
                                            onClick={() => onBuyerClick?.(s)}
                                            className={`transition-colors ${onBuyerClick ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50'}`}
                                            title={onBuyerClick ? 'Open buyer profile' : undefined}
                                        >
                                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                                <div>{formatMelbourneShortDate(s.saved_at)}</div>
                                                <div className="text-xs text-gray-500">{formatMelbourneClock(s.saved_at)}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-medium text-gray-900">{s.buyer_name || '—'}</div>
                                                <div className="text-xs text-gray-500">
                                                    {s.buyer_email}{s.buyer_phone ? ` · ${s.buyer_phone}` : ''}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                {s.address ? (
                                                    <a
                                                        href={`/properties/${s.property_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-teal-700 hover:underline"
                                                    >
                                                        {s.address}
                                                    </a>
                                                ) : (
                                                    <span className="text-gray-400">Property #{s.property_id}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{s.agent_name || '—'}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${s.still_saved
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {s.still_saved ? 'Saved' : 'Removed'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-600">
                        Showing {visibleSaves.length.toLocaleString()} of {saves.length.toLocaleString()} save{saves.length !== 1 ? 's' : ''}
                        {data?.truncated ? ' (newest only, narrow the date range to see older saves)' : ''}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PropertySavesModal;
